import { Box, Button, Container, IconButton, Link, Menu, MenuItem, Stack, SvgIcon, useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { ReactComponent as LandingLogo } from 'assets/images/landing-logo.svg';
import { ReactComponent as OutlinedButton } from 'assets/images/landing/buttons/header-outline-button.svg';
import { ReactComponent as ContainedButton } from 'assets/images/landing/buttons/header-contained-button.svg';

import LandingButton from 'sections/landing/components/LandingButton';
import { LandingSection } from 'types/landing-section';

const LINKS = [
  {
    label: 'Our services',
    path: `#${LandingSection.SERVICES}`
  },
  {
    label: 'How it works',
    path: `#${LandingSection.HOW_IT_WORKS}`
  },
  {
    label: 'Benefits',
    path: `#${LandingSection.BENEFITS}`
  },
  {
    label: 'Use Cases',
    path: `#${LandingSection.USE_CASES}`
  }
];

const Header = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{ bgcolor: 'white', color: theme.palette.text.primary, paddingBlock: 3, borderBottom: `1px solid ${theme.palette.grey[100]}` }}
    >
      <Container
        sx={{ display: 'flex', alignItems: 'center', gap: { xs: 4, lg: '89px' }, justifyContent: { xs: 'space-between', md: 'start' } }}
      >
        <Link component={RouterLink} to="/" color="text.primary">
          <LandingLogo />
        </Link>
        <Stack
          component="nav"
          direction="row"
          gap={5}
          sx={{
            display: { xs: 'none', md: 'flex' },
            fontFamily: 'Lato, sans-serif',
            flex: '1 0 auto',
            '& .header-link': {
              color: theme.palette.grey[600],

              '&:hover': {
                color: theme.palette.primary[900]
              }
            }
          }}
        >
          {LINKS.map(({ label, path }) => (
            <Link key={label} onClick={() => navigate('/')} href={path} fontSize="1rem" className="header-link" underline="none">
              {label}
            </Link>
          ))}
        </Stack>
        <Stack direction="row" gap={2} display={{ xs: 'none', md: 'flex' }}>
          <LandingButton href="#" svg={<OutlinedButton />} />
          <LandingButton href="#" svg={<ContainedButton />} />
        </Stack>

        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleOpenNavMenu}
            color="inherit"
          >
            <SvgIcon>
              <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium" focusable="false" aria-hidden="true" viewBox="0 0 24 24">
                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"></path>
              </svg>
            </SvgIcon>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorElNav}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{
              display: { xs: 'block', md: 'none' },
              maxWidth: '230px',
              width: '100%'
            }}
            PaperProps={{
              sx: {
                borderRadius: 4
              }
            }}
          >
            {LINKS.map(({ label, path }) => (
              <MenuItem
                key={label}
                onClick={handleCloseNavMenu}
                sx={{
                  alignItems: 'stretch'
                }}
              >
                <Link
                  sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    color: theme.palette.grey[600],

                    '&:hover': {
                      color: theme.palette.primary.main
                    }
                  }}
                  underline="none"
                  href={path}
                >
                  {label}
                </Link>
              </MenuItem>
            ))}
            <MenuItem>
              <Button onClick={handleCloseNavMenu} variant="outlined" size="small" fullWidth>
                Apply as a Talent
              </Button>
            </MenuItem>
            <MenuItem>
              <Button onClick={handleCloseNavMenu} variant="contained" size="small" fullWidth>
                Get started
              </Button>
            </MenuItem>
          </Menu>
        </Box>
      </Container>
    </AppBar>
  );
};

export default Header;
